import React from "react";
import { useTranslation } from "react-i18next";
import { ShoppingBag, Loader2 } from "lucide-react";
import { useOrderProcessing } from "../hooks/use-order-processing";

/**
 * Ödeme tamamlandıktan sonra sipariş işlenirken gösterilen tam ekran yükleme ekranı.
 * Saniye başı /shop/checkout/status/{orderGroupId} endpoint'ini polling yapar.
 * COMPLETED → success sayfasına yönlendirir
 * FAILED    → hata toast'u gösterip kapanır
 */
export const OrderProcessingScreen: React.FC = () => {
  const { t } = useTranslation();
  const { isProcessingScreenOpen } = useOrderProcessing();

  if (!isProcessingScreenOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-white/95 dark:bg-gray-950/95 backdrop-blur-sm">
      <div className="flex flex-col items-center gap-8 px-6 text-center max-w-sm">
        {/* Icon with pulsing ring */}
        <div className="relative">
          {/* Outer pulsing rings */}
          <span className="absolute inset-0 rounded-full bg-indigo-100 dark:bg-indigo-900/30 animate-ping opacity-60" />
          <span className="absolute inset-[-8px] rounded-full bg-indigo-50 dark:bg-indigo-900/20 animate-ping opacity-40 animation-delay-300" />

          {/* Icon container */}
          <div className="relative w-20 h-20 rounded-full bg-indigo-50 dark:bg-indigo-900/40 border border-indigo-100 dark:border-indigo-800 flex items-center justify-center">
            <ShoppingBag className="w-9 h-9 text-indigo-500 dark:text-indigo-400" />
          </div>
        </div>

        {/* Texts */}
        <div className="flex flex-col gap-2">
          <h2 className="text-xl font-semibold text-text-primary dark:text-white">
            {t("checkout.processing.title")}
          </h2>
          <p className="text-sm text-text-tertiary dark:text-gray-400 leading-relaxed">
            {t("checkout.processing.description")}
          </p>
        </div>

        {/* Spinner + status text */}
        <div className="flex items-center gap-2.5 text-indigo-500 dark:text-indigo-400">
          <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
          <span className="text-sm font-medium">
            {t("checkout.processing.status")}
          </span>
        </div>

        {/* Progress dots */}
        <div className="flex gap-1.5">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="w-2 h-2 rounded-full bg-indigo-300 dark:bg-indigo-600 animate-bounce"
              style={{ animationDelay: `${i * 150}ms` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
